import React, { useMemo } from 'react'
import type { FormControlProps as MUIFormControlProps } from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'
import type { InputLabelProps as MUIInputLabelProps } from '@mui/material/InputLabel'
import MUISelect from '@mui/material/Select'
import type {
	SelectProps as MUISelectProps,
	SelectChangeEvent as MUISelectChangeEvent,
} from '@mui/material/Select'
import FormHelperText from '@mui/material/FormHelperText'
import MenuItem from '@mui/material/MenuItem'
import MUIOutlinedInput from '@mui/material/OutlinedInput'
import MUIChip from '@mui/material/Chip'
import Box from '@mui/material/Box'
import { Controller, useFormContext } from 'react-hook-form'
import type { FieldErrors } from 'react-hook-form'

import useFormField from '../../hooks/useFormField'
import { RFHControl } from '../../types'
import FormControl from './FormControl'

type ItemProps<T> = {
	disabled?: boolean
} & T

export type SelectChangeEvent<T> = MUISelectChangeEvent<T>

export type MultiSelectProps<T> = {
	name?: string
	menuItems: ItemProps<T>[]
	children?: React.ReactNode
	labelField?: string
	valueField?: string
	control?: RFHControl
	errors?: FieldErrors
	helperText?: string
	selectProps?: Omit<MUISelectProps, 'disabled' | 'displayEmpty'>
	labelProps?: MUIInputLabelProps
	renderItem?: (item: ItemProps<T>) => React.ReactNode
	displayEmpty?: boolean
	label?: string
	placeholder?: string
	disabled?: boolean
	controlled?: boolean
} & MUIFormControlProps

const MultiSelect = <T,>({
	variant = 'standard',
	name,
	selectProps,
	labelProps,
	label,
	placeholder,
	children,
	errors: propsErrors,
	control: propsControl,
	defaultValue,
	displayEmpty,
	menuItems,
	labelField = 'label',
	valueField = 'value',
	renderItem,
	helperText,
	disabled,
	controlled = true,
	...rest
}: MultiSelectProps<T>) => {
	const formContext = useFormContext()
	const control = propsControl ?? formContext?.control
	const errors = propsErrors ?? formContext?.formState.errors

	const { getError } = useFormField()
	const error = getError(name, errors)
	const labelId = `${name ?? label}-label`

	const EmptyMenuItem = useMemo(
		() =>
			displayEmpty ? (
				<MenuItem value="">
					<em>{placeholder ?? 'Select value'}</em>
				</MenuItem>
			) : undefined,
		[displayEmpty, placeholder]
	)

	const MenuItems: React.ReactNode[] | undefined = useMemo(
		() =>
			menuItems
				? menuItems.map((item: ItemProps<T>, i) =>
						renderItem ? (
							renderItem(item)
						) : (
							<MenuItem
								key={`${name}-menu-item-${i}`}
								value={
									item[valueField as keyof ItemProps<T>] as string
								}
								disabled={item.disabled}
							>
								{item[labelField as keyof ItemProps<T>] as string}
							</MenuItem>
						)
					)
				: undefined,
		[labelField, menuItems, name, renderItem, valueField]
	)

	const getChipLabel = (value: string) =>
		menuItems.find(
			(item) =>
				!!item && item[valueField as keyof ItemProps<T>] === value
		)?.[labelField as keyof ItemProps<T>] as string

	const renderControlled = control && name && controlled

	return (
		<FormControl {...rest} variant={variant} error={!!error}>
			{label && (
				<InputLabel {...labelProps} id={labelId}>
					{label}
				</InputLabel>
			)}
			{renderControlled ? (
				<Controller
					name={name}
					control={control}
					defaultValue={defaultValue}
					render={({ field }) => {
						const handleSelectChange = (
							event: SelectChangeEvent<unknown>,
							child: React.ReactNode
						) => {
							field.onChange(event)
							selectProps?.onChange?.(event, child)
						}

						return (
							<MUISelect
								{...selectProps}
								{...field}
								onChange={handleSelectChange}
								disabled={disabled}
								labelId={labelId}
								input={
									<MUIOutlinedInput
										id={`select-multiple-chip-${name}`}
										label={label}
									/>
								}
								renderValue={(selected: string[]) => {
									return (
										<Box className="flex flex-wrap gap-1">
											{selected.map((value: string) => (
												<MUIChip
													key={value}
													label={getChipLabel(value)}
												/>
											))}
										</Box>
									)
								}}
							>
								{EmptyMenuItem}
								{MenuItems}
								{children}
							</MUISelect>
						)
					}}
				/>
			) : (
				<MUISelect
					name={name}
					defaultValue={defaultValue}
					{...selectProps}
					disabled={disabled}
					labelId={labelId}
					input={
						<MUIOutlinedInput
							id={`select-multiple-chip-${name}`}
							label={label}
						/>
					}
					renderValue={(selected: unknown) => {
						if (!selected || !Array.isArray(selected))
							return undefined
						return (
							<Box className="flex flex-wrap gap-1">
								{selected?.map((value: string) => (
									<MUIChip key={value} label={getChipLabel(value)} />
								))}
							</Box>
						)
					}}
				>
					{EmptyMenuItem}
					{MenuItems}
					{children}
				</MUISelect>
			)}
			{helperText && <FormHelperText>{helperText}</FormHelperText>}
			{error && <FormHelperText>{error.message}</FormHelperText>}
		</FormControl>
	)
}

export default MultiSelect
